import type { Theme } from '@mui/material';
import { Paper, Typography, useTheme } from '@mui/material';
import type { DefaultizedPieValueType } from '@mui/x-charts/models';
import { PieChart } from '@mui/x-charts/PieChart';
import type { GetRevenueByCurrencyResponse } from '@weft/api-definition';
import type { Currency } from '@weft/types';
import { CURRENCIES, CurrencySchema } from '@weft/types';
import { entries, isNotNullOrUndefined, raiseUnreachable } from '@weft/utils';
import { PieChartHoverWrapper } from 'apps/app/src/components/new-design/charts/pie-chart/PieChartHoverWrapper';
import { useState } from 'react';

type RevenueByCurrencyPieChartProps = {
  amountByCurrency: GetRevenueByCurrencyResponse['amountByCurrency']
};

export const RevenueByCurrencyPieChart: React.FC<RevenueByCurrencyPieChartProps> = ({ amountByCurrency }) => {
  const vyTheme = useTheme();
  const [lastHoveredId, setLastHoveredId] = useState<Currency | null>(null);

  const handleChartHover = (itemData: DefaultizedPieValueType) => {
    const unparsedId = String(itemData.id);
    const parsedId = CurrencySchema.safeParse(unparsedId);

    if (parsedId.success) {
      setLastHoveredId(parsedId.data);
    }
  };

  return (
    <PieChart
      slotProps={{ legend: { hidden: true } }}
      tooltip={{
        trigger: 'item',
        itemContent: (props) => (
          <PieChartHoverWrapper
            onChartHover={handleChartHover}
            pieChartHoverProps={props}
            render={(itemData) => (
              <Paper sx={{
                backgroundColor: '#0D1B27DE',
                padding: 1,
                transform: 'translate(-50%, -120%)',
              }}
              >
                <Typography
                  fontSize="14px"
                  lineHeight="18px"
                  fontWeight={500}
                  textAlign="center"
                  color="white"
                >
                  {`${itemData.label}`}
                </Typography>
              </Paper>
            )}
          />
        ),
      }}
      series={[
        {
          highlightScope: isNotNullOrUndefined(lastHoveredId) ? { faded: 'none', highlighted: 'item' } : undefined,
          highlighted: isNotNullOrUndefined(lastHoveredId) ? { color: getBaseColorByCurrency(lastHoveredId, vyTheme).dark } : undefined,
          data: entries(amountByCurrency).map(([currency, amount]) => ({
            id: currency,
            value: amount ?? 0,
            label: currency,
            color: getBaseColorByCurrency(currency, vyTheme).light,
          })),
          innerRadius: 0,
          outerRadius: 120,
          paddingAngle: 0.5,
          cornerRadius: 0,
          startAngle: 0,
          endAngle: 360,
          cx: 115,
        },
      ]}
    />
  );
};

function getBaseColorByCurrency(currency: Currency, theme: Theme) {
  switch (currency) {
    case CURRENCIES.Usd:
      return theme.palette.success;
    case CURRENCIES.Eur:
      return theme.palette.primary;
    case CURRENCIES.Gbp:
      return theme.palette.warning;
    case CURRENCIES.Ils:
      return theme.palette.error;
    default:
      return raiseUnreachable(currency);
  }
}